import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api/http.js";
import { useSession } from "../state/session.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { Link } from "../router.js";
import { AuthFrame } from "./AuthFrame.js";
import { errorKey } from "./Login.js";

export function Register() {
  const t = useT();
  const register = useSession((s) => s.register);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [invite, setInvite] = useState("");
  const [needsInvite, setNeedsInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The server decides; the field only appears where it is needed, and after an invite answer at the latest.
  useEffect(() => {
    let alive = true;
    void api<{ inviteRequired: boolean }>("GET", "/api/health").then(
      (health) => { if (alive && health.inviteRequired) setNeedsInvite(true); },
      () => undefined,
    );
    return () => { alive = false; };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t("auth.password.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await register(name.trim(), password, invite.trim());
    } catch (err) {
      if (err instanceof ApiError && err.code.startsWith("INVITE_")) setNeedsInvite(true);
      setError(t(errorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <Panel title={t("auth.register")}>
        <form onSubmit={submit} data-testid="register-form">
          <div className="field">
            <label htmlFor="reg-name">{t("auth.name")}</label>
            <input id="reg-name" autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={20} pattern="[A-Za-z0-9_]{3,20}" autoFocus />
            <span className="hint">{t("auth.name.hint")}</span>
          </div>
          <div className="field">
            <label htmlFor="reg-password">{t("auth.password")}</label>
            <input id="reg-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} maxLength={128} />
            <span className="hint">{t("auth.password.hint")}</span>
          </div>
          <div className="field">
            <label htmlFor="reg-confirm">{t("auth.password.confirm")}</label>
            <input id="reg-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          {needsInvite ? (
            <div className="field">
              <label htmlFor="reg-invite">{t("auth.invite")}</label>
              <input id="reg-invite" data-testid="reg-invite" autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="ABCD-EFGH" maxLength={20} value={invite} onChange={(e) => setInvite(e.target.value)} required />
              <span className="hint">{t("auth.invite.hint")}</span>
            </div>
          ) : null}
          {error ? <p className="error" role="alert">{error}</p> : null}
          <Button type="submit" variant="gold" loading={busy} style={{ width: "100%" }}>
            {busy ? t("auth.submitting") : t("auth.register")}
          </Button>
        </form>
        <p className="dim" style={{ marginTop: 16, fontSize: 14 }}>
          {t("auth.haveAccount")} <Link to="/login">{t("auth.login")}</Link>
        </p>
      </Panel>
    </AuthFrame>
  );
}
